/**
 * extensions/dicom-rt/src/modules/dicom-measurement/src/assessments/psmaPosVolumeUtils.js
 *
 * Histogram query utilities for RECIP 1.0 PSMA+ volume computation.
 *
 * These functions are kept separate from computePsmaPosVolumesFromBridge so
 * they can be imported by tests without pulling in the full compute chain.
 *
 * Histogram spec (from computeRoiSuv E-1):
 *   binWidth  = 0.05 SUV units
 *   binCount  = 400  → covers SUV 0.00–20.00
 *   overflow  = count of voxels with SUV ≥ 20
 *   bins[i]   = count of voxels with SUV ∈ [i × 0.05, (i+1) × 0.05)
 *
 * Query cost: ~400 integer additions. Negligible.
 */

/**
 * Count voxels with SUV > thresholdSuv.
 * Note: strictly GREATER THAN (not ≥) — threshold voxels are PSMA-negative.
 * The first bin that qualifies starts at the first bin where ALL voxels exceed
 * the threshold, i.e. bins[i] where i × binWidth > thresholdSuv.
 *
 * @param {{ binWidth, binCount, bins: Int32Array, overflow }} histogram
 * @param {number} thresholdSuv
 * @returns {number}  count of qualifying voxels
 */
export function countVoxelsAboveThreshold(histogram, thresholdSuv) {
  if (!histogram || !isFinite(thresholdSuv) || thresholdSuv < 0) return 0;
  const { binWidth, binCount, bins, overflow } = histogram;

  // The first bin fully above threshold: bin index i where i * binWidth > thresholdSuv
  // i.e. i > thresholdSuv / binWidth → i = floor(thresholdSuv / binWidth) + 1
  // This correctly excludes any bin that spans the threshold boundary, which is
  // the conservative choice matching the "strictly greater than" RECIP definition.
  const firstBin = Math.floor(thresholdSuv / binWidth) + 1;

  if (firstBin >= binCount) return overflow;
  let count = overflow;
  for (let i = firstBin; i < binCount; i++) count += bins[i];
  return count;
}

/**
 * Compute PSMA+ volume in mm³ for one ROI at one timepoint.
 *
 * @param {{ binWidth, binCount, bins, overflow }} histogram  from bridge entry
 * @param {number} thresholdSuv                               bone or soft-tissue threshold
 * @param {number} voxelVolumeMm3                             from bridge entry petVoxelVolumeMm3
 * @returns {number}  mm³ (can be 0 if no voxels exceed threshold)
 */
export function psmaPosVolumeMm3(histogram, thresholdSuv, voxelVolumeMm3) {
  return countVoxelsAboveThreshold(histogram, thresholdSuv) * voxelVolumeMm3;
}

/**
 * Build a synthetic histogram from an explicit voxel SUV list.
 * Used exclusively in tests — real production histograms come from
 * computeRoiSuv. Passing real SUV values produces predictable test fixtures.
 *
 * @param {number[]} suvValues  array of per-voxel SUV values
 * @param {number}   [binWidth=0.05]
 * @param {number}   [binCount=400]
 * @returns {{ binWidth, binCount, bins: Int32Array, overflow }}
 */
export function buildTestHistogram(suvValues, binWidth = 0.05, binCount = 400) {
  const bins = new Int32Array(binCount);
  let overflow = 0;
  for (const suv of suvValues) {
    if (suv < 0) continue;
    const idx = Math.floor(suv / binWidth);
    if (idx >= binCount) overflow++;
    else bins[idx]++;
  }
  return { binWidth, binCount, bins, overflow };
}
